import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Check, Building2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const TYPES = ["Contractor", "Builder", "Developer", "Architect", "Interior Designer", "Engineer", "Construction Company", "Supplier", "Vendor"];

export default function BusinessOnboarding() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    name: "", business_type: "Contractor", gstin: "", pan: "", city: "", address: "",
    phone: "", employees: "1-10", active_projects: "1-5", annual_volume: "< ₹5 Cr",
  });
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async () => {
    if (!f.name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await api.post("/organizations", f);
      await refresh();
      toast.success("Organization created!");
      nav("/app/business");
    } catch { toast.error("Could not create organization."); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen bg-sp-light">
      <div className="bg-sp-navy text-white"><div className="sp-container py-5 flex items-center justify-between"><Logo dark /><span className="text-sm text-white/60">Sitepar Business Onboarding</span></div></div>
      <div className="sp-container max-w-2xl py-10">
        <div className="bg-white rounded-xl border border-slate-200 p-7" data-testid="business-onboarding">
          <div className="h-12 w-12 rounded-lg bg-sp-navy flex items-center justify-center mb-5"><Building2 className="h-6 w-6 text-sp-orange" /></div>
          <h1 className="text-2xl font-bold text-sp-navy">Create your organization</h1>
          <p className="text-sm text-slate-500 mt-1">Set up your construction business workspace.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            <F label="Company name" full><Input data-testid="bob-name" value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="Apex Buildworks" /></F>
            <F label="Business type"><Select value={f.business_type} onValueChange={(v) => set("business_type", v)}><SelectTrigger data-testid="bob-type"><SelectValue /></SelectTrigger><SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></F>
            <F label="City"><Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru" /></F>
            <F label="GSTIN"><Input value={f.gstin} onChange={(e) => set("gstin", e.target.value)} /></F>
            <F label="PAN"><Input value={f.pan} onChange={(e) => set("pan", e.target.value)} /></F>
            <F label="Office address" full><Input value={f.address} onChange={(e) => set("address", e.target.value)} /></F>
            <F label="Contact phone"><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} /></F>
            <F label="Employees"><Select value={f.employees} onValueChange={(v) => set("employees", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["1-10", "11-50", "50-200", "200+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></F>
            <F label="Active projects"><Select value={f.active_projects} onValueChange={(v) => set("active_projects", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["1-5", "6-20", "20-50", "50+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></F>
            <F label="Annual volume"><Select value={f.annual_volume} onValueChange={(v) => set("annual_volume", v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["< ₹5 Cr", "₹5-25 Cr", "₹25-100 Cr", "₹100 Cr+"].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select></F>
          </div>
          <Button data-testid="bob-submit" disabled={saving} onClick={submit} className="mt-8 w-full h-11 bg-sp-orange hover:bg-sp-orangedark rounded-lg">{saving ? "Creating..." : "Create Organization"} <ArrowRight className="ml-1 h-4 w-4" /></Button>
        </div>
      </div>
    </div>
  );
}
function F({ label, children, full }) { return <div className={full ? "sm:col-span-2" : ""}><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div></div>; }
